import { ComputerDesktopIcon } from '@heroicons/react/24/outline';
import type { MeuEquipamento } from '@/features/SI/services';
import { normalizarPatrimonio } from '@/features/SI/models/equipamento.utils';
import { descreverEquipamentoResumo } from '@/features/SI/utils/descreverEquipamentoResumo';
import {
  CATEGORIAS_ATIVO_ABA,
  categoriaDoTipo,
} from '@/features/SI/pages/EquipamentosListPage/components/Ativos/ativos.definitions';
import { SiPortalEmBreve } from '../../../components/SiPortalEmBreve';

/** Itens simples: não faz sentido "Pedir troca" (troca é de equipamento com defeito/dano). */
const TIPOS_SEM_TROCA = ['chip', 'cartao_acesso'];

const BOTAO_CLASSE =
  'rounded-full border border-raro-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-raro-gray-700 transition-colors hover:border-primary-300 hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500';

/** Quantas colunas (de 2 no md, 3 no xl) cada categoria ocupa — o mínimo entre itens e colunas.
 * Classes fixas (o Tailwind não gera classe montada em tempo de execução). */
const LARGURA_POR_ITENS: Record<1 | 2 | 3, { secao: string; lista: string }> = {
  1: { secao: '', lista: '' },
  2: { secao: 'md:col-span-2 xl:col-span-2', lista: 'md:grid-cols-2' },
  3: { secao: 'md:col-span-2 xl:col-span-3', lista: 'md:grid-cols-2 xl:grid-cols-3' },
};

interface MeusEquipamentosProps {
  equipamentos: MeuEquipamento[];
  isLoading: boolean;
  isError: boolean;
  onDevolver: (equipamento: MeuEquipamento) => void;
  onPedirTroca: (equipamento: MeuEquipamento) => void;
  onAcompanhar: (solicitacaoId: string) => void;
  onInformarDivergencia: () => void;
}

function situacao(equipamento: MeuEquipamento): { label: string; classe: string } {
  if (equipamento.transitoSentido === 'envio') return { label: 'A caminho', classe: 'bg-blue-100 text-blue-800' };
  if (equipamento.transitoSentido === 'devolucao') return { label: 'Em devolução', classe: 'bg-amber-100 text-amber-800' };
  return { label: 'Em uso', classe: 'bg-green-100 text-green-800' };
}

function linhaSecundaria(equipamento: MeuEquipamento): string {
  const partes = [
    equipamento.transitoSentido === 'envio'
      ? 'A caminho de você'
      : equipamento.desde
        ? `Com você desde ${new Date(equipamento.desde).toLocaleDateString('pt-BR')}`
        : null,
    equipamento.projetoNome ? `Projeto ${equipamento.projetoNome}` : null,
  ];
  return partes.filter(Boolean).join(' · ');
}

/**
 * Aba "Meus equipamentos" (Fase 2 do Portal do SI): o que está com a pessoa pelo Inventário,
 * agrupado como as abas do Inventário, grade única de 3 colunas em que cada categoria ocupa só as colunas que precisa
 * (1 item = 1 coluna; 3+ = linha toda, itens lado a lado dentro) — categorias pequenas dividem
 * a linha e não sobra espaço em branco. Na mesma linha, todos os cards têm a mesma altura (botões
 * sempre no rodapé), mesmo quando um item tem menos informação (ex.: chip, sem nº de série). Equipamentos do cliente ficam junto (o SI controla
 * envio/recolhimento deles também), só com o selo "Do cliente X".
 */
export function MeusEquipamentos({
  equipamentos,
  isLoading,
  isError,
  onDevolver,
  onPedirTroca,
  onAcompanhar,
  onInformarDivergencia,
}: MeusEquipamentosProps) {
  const grupos = [...CATEGORIAS_ATIVO_ABA, { id: 'outros' as const, label: 'Outros', tipos: [] as string[] }]
    .map((categoria) => ({
      ...categoria,
      itens: equipamentos
        .filter((e) => categoriaDoTipo(e.tipo) === categoria.id)
        .sort((a, b) =>
          (normalizarPatrimonio(a.patrimonio) ?? '').localeCompare(normalizarPatrimonio(b.patrimonio) ?? '', 'pt-BR', { numeric: true })
        ),
    }))
    .filter((grupo) => grupo.itens.length > 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-2xl border border-primary-100 bg-white/70 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-raro-gray-600">
          O que está com você hoje, pelo Inventário do SI. <b>Algo diferente do que você tem?</b> Avise o SI.
        </p>
        <button
          type="button"
          data-tour="si-sol-divergencia"
          onClick={onInformarDivergencia}
          className="shrink-0 rounded-full border border-primary-200 bg-white px-4 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
        >
          Informar divergência
        </button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="h-32 animate-pulse rounded-2xl bg-raro-gray-100" />
          <div className="h-32 animate-pulse rounded-2xl bg-raro-gray-100" />
        </div>
      ) : isError ? (
        <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Não foi possível carregar seus equipamentos. Tente de novo em instantes.
        </p>
      ) : grupos.length === 0 ? (
        <SiPortalEmBreve
          icon={ComputerDesktopIcon}
          titulo="Nenhum equipamento no seu nome"
          descricao='Se você está com algum equipamento da Raro ou do cliente, use "Informar divergência" para o SI corrigir o Inventário.'
        />
      ) : (
        <div className="grid grid-flow-row-dense gap-4 md:grid-cols-2 xl:grid-cols-3">
          {grupos.map((grupo) => {
            const largura = LARGURA_POR_ITENS[Math.min(grupo.itens.length, 3) as 1 | 2 | 3];
            return (
              <section
                key={grupo.id}
                className={`flex flex-col overflow-hidden rounded-2xl border border-raro-gray-200 bg-white shadow-sm ${largura.secao}`}
              >
                <header className="flex justify-between border-b border-raro-gray-100 bg-raro-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-raro-gray-600">
                  {grupo.label} <span>{grupo.itens.length}</span>
                </header>
                <ul className={`grid flex-1 auto-rows-fr gap-3 p-3 ${largura.lista}`}>
                  {grupo.itens.map((equipamento) => {
                    const { tipo, detalhe } = descreverEquipamentoResumo(equipamento);
                    const { label, classe } = situacao(equipamento);
                    const emTransito = equipamento.transitoSentido != null;
                    return (
                      <li
                        key={equipamento.id}
                        className="flex min-h-[8.5rem] flex-col justify-between gap-3 rounded-xl border border-raro-gray-200 px-4 py-3.5"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-raro-gray-900">
                              {detalhe}
                              {grupo.tipos.length !== 1 && <span className="ml-1 text-xs font-normal text-raro-gray-500">({tipo})</span>}
                              {equipamento.clienteProprietarioNome && (
                                <span className="ml-2 whitespace-nowrap rounded-full bg-raro-gray-100 px-2 py-0.5 text-xs font-semibold text-raro-gray-600">
                                  Do cliente {equipamento.clienteProprietarioNome}
                                </span>
                              )}
                            </p>
                            {equipamento.nSerie && equipamento.tipo !== 'chip' && (
                              <p className="text-xs text-raro-gray-500">Nº de série {equipamento.nSerie}</p>
                            )}
                            <p className="mt-1 text-xs text-raro-gray-500">{linhaSecundaria(equipamento)}</p>
                          </div>
                          <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${classe}`}>{label}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {emTransito && equipamento.transitoSolicitacaoId ? (
                            <button
                              type="button"
                              className={BOTAO_CLASSE}
                              onClick={() => onAcompanhar(equipamento.transitoSolicitacaoId as string)}
                            >
                              {equipamento.transitoSentido === 'envio' ? 'Acompanhar envio' : 'Acompanhar devolução'}
                            </button>
                          ) : (
                            <>
                              {equipamento.termoUrl && (
                                <a href={equipamento.termoUrl} target="_blank" rel="noreferrer" className={BOTAO_CLASSE}>
                                  Ver termo assinado
                                </a>
                              )}
                              {!TIPOS_SEM_TROCA.includes(equipamento.tipo ?? '') && (
                                <button type="button" className={BOTAO_CLASSE} onClick={() => onPedirTroca(equipamento)}>
                                  Pedir troca
                                </button>
                              )}
                              <button type="button" className={BOTAO_CLASSE} onClick={() => onDevolver(equipamento)}>
                                Devolver
                              </button>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
